import {
  Block,
  WorkflowRun,
  isWorkflowBlock,
} from '@basehub/data/replicache/index'
import { useWorkingTreeStore } from '../blocks-tree/provider'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { useRepoReplicache } from '../use-repo-replicache'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'

type GenerationRuns = {
  running: WorkflowRun[]
  pending: WorkflowRun[]
  failed: WorkflowRun | undefined
}

/**
 * Returns the running, scheduled and (if failed), the last run for a certain block
 * @param {string} blockId - The block id to retrieve generation runs
 * @returns GenerationRuns - Object with the running, pending and failed runs
 */
export const useActiveBlockGenerations = (blockId: string): GenerationRuns => {
  const replicache = useRepoReplicache().data?.replicache
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  // use replicacheOrUndefined to disable the syncer if branch or commit isn't resolved yet
  const replicacheOrUndefined = !selectedBranchOrCommitQuery.data
    ? undefined
    : replicache
  const tree = useWorkingTreeStore((s) => s.tree)
  const pendingAndRunningIds: string[] =
    useTreeSlice(tree, (s) => {
      const ids: string[] = []
      const index = s.compoundIndexes.get('blocksByType') as Map<
        Block['type'],
        Set<string>
      >
      const workflows = index.get('workflow')
      if (!workflows) return ids

      Array.from(workflows).forEach((id) => {
        const workflow = s.getNode(id)?.value
        if (!workflow) return
        if (!isWorkflowBlock(workflow)) return []

        workflow.value?.activeRuns.forEach((r) => {
          if (r.action.type !== 'ai-autofill') return
          if (r.action.blockId !== blockId) return

          if (r.status === 'running') ids.push(r.id)
          else if (r.status === 'pending') ids.push(r.id)
        })
      })

      return ids
    }) ?? []

  const { data: lastRunArray } = useCustomSyncerSub(replicacheOrUndefined, {
    syncer: 'workflowAutofillRunsByBlockId',
    params: {
      blockId,
      limit: 1,
      branchId: selectedBranchOrCommitQuery.data?.id ?? '',
    },
  })
  const { data: pendingAndRunning } = useCustomSyncerSub(
    replicacheOrUndefined,
    {
      syncer: 'workflowRunsById',
      params: {
        ids: pendingAndRunningIds,
        limit: 15,
        branchId: selectedBranchOrCommitQuery.data?.id ?? '',
      },
    }
  )

  const running: WorkflowRun[] = []
  const pending: WorkflowRun[] = []
  let failed: WorkflowRun | undefined
  ;(pendingAndRunning ?? []).forEach((run) => {
    if (run.status === 'running') {
      running.push(run)
    } else if (run.status === 'pending') {
      pending.push(run)
    }
  })
  const [lastRun] = lastRunArray ?? []
  if (lastRun && lastRun.status === 'error') {
    failed = lastRun
  }

  return { running, pending, failed }
}
